/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import swal from "sweetalert";

/**
 * Custom hook for search and filter functionality
 */
export const useSearchAndFilter = (state) => {
  const { t } = useTranslation();
  const { setSearchTerm, setSelectedTags, setLoading, searchTerm, selectedTags } = state;
  const { ApiDocuments } = require("../ApiDocuments");

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchTerm(value);
    },
    [setSearchTerm]
  );

  const handleSearchTagsChange = useCallback(
    (newValue) => {
      setSelectedTags(newValue || []);
    },
    [setSelectedTags]
  );

  const handleSearch = useCallback(() => {
    state.setDetailBox(false);
    setLoading(true);
    ApiDocuments()
      .getDocuments({
        folderUnitId: state.folderUnitId,
        folderId: state.folderId,
        searchString: searchTerm,
        tags: selectedTags,
      })
      .then((res) => {
        state.handleData(res);
        state.setHoveredRowIndex(null);
        state.setHierarchyDisable(false);
        state.setResultFromSearch(res.data.resultFromSearch);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        swal({
          title: err,
          icon: "error",
          buttons: "OK",
        });
      });
  }, [state, searchTerm, selectedTags, setLoading, t]);

  const handleClearSearch = useCallback(() => {
    if (searchTerm !== "" || selectedTags.length !== 0) {
      setSearchTerm("");
      setSelectedTags([]);
      state.setClearSearch(true);
    }
  }, [searchTerm, selectedTags, setSearchTerm, setSelectedTags, state]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleSearch();
      } else if (event.key === "Escape") {
        handleClearSearch();
      }
    },
    [handleSearch, handleClearSearch]
  );

  return {
    handleSearchChange,
    handleSearchTagsChange,
    handleKeyDown,
    handleSearch,
    handleClearSearch,
  };
};

/**
 * Custom hook for dropzone configuration
 */
export const useDropzoneConfig = (state) => {
  const { t } = useTranslation();
  const {
    setError,
    setDroppedFile,
    setDroppedFolder,
    folderParentUnitIds,
    unitFolderUnitId,
    resultFromSearch,
  } = state;

  const clearErrorAfterDelay = useCallback(() => {
    setTimeout(() => {
      setError(null);
    }, 3000);
  }, [setError]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const files = acceptedFiles.filter((file) => !file.path.includes("/"));
      const folders = acceptedFiles.filter((file) => file.path.includes("/"));

      // Check if both files and folders are dropped
      if (files.length > 0 && folders.length > 0) {
        setError(t("document_files_and_folder_upload_error"));
        clearErrorAfterDelay();
        return;
      }

      if (
        acceptedFiles.length > 0 &&
        acceptedFiles.every((e) => e.path.includes("/"))
      ) {
        if (
          !folderParentUnitIds.includes(unitFolderUnitId) &&
          !resultFromSearch
        ) {
          const rootFolders = new Set(
            acceptedFiles
              .map((file) => file.path.split("/")[1])
              .filter(Boolean)
          );

          if (rootFolders.size === 1) {
            setDroppedFolder(acceptedFiles);
          } else {
            setError(t("document_multiple_folder_upload_error"));
            clearErrorAfterDelay();
          }
        }
      } else {
        if (
          !folderParentUnitIds.includes(unitFolderUnitId) &&
          !resultFromSearch
        ) {
          const maxSizeBytes = 26214400; // 26 MB

          for (const uploadedFile of acceptedFiles) {
            if (uploadedFile.size > maxSizeBytes) {
              setError(t("file_size_limit_exceed_error"));
              clearErrorAfterDelay();
              return;
            }

            const fileType =
              uploadedFile.type ||
              uploadedFile.name.split(".").pop().toLowerCase();
            if (
              fileType === "exe" ||
              fileType === "msi" ||
              fileType === "application/x-msdownload"
            ) {
              setError(t("file_type_error"));
              clearErrorAfterDelay();
              return;
            }
          }

          setDroppedFile(acceptedFiles);
          setError(null);
        }
      }
    },
    [
      t,
      setError,
      setDroppedFile,
      setDroppedFolder,
      folderParentUnitIds,
      unitFolderUnitId,
      resultFromSearch,
      clearErrorAfterDelay,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    directory: true,
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    clearErrorAfterDelay,
  };
};
